/**
 * Use the Compact Promotional Group to feature 5+ promoted stories in a compact way that are related. For example, Beauty steals during a major event. Use when a higher prominence and/or creative image is needed.
 *
 * @module views/components/FeaturedRail
 * @memberof -Common
 */
import './FeaturedRail.scss';

import React, { useRef } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import ActionGroups from '@ulta/core/components/ActionGroups/ActionGroups';
import GridContainer from '@ulta/core/components/GridContainer/GridContainer';
import Image from '@ulta/core/components/Image/Image';
import Text from '@ulta/core/components/Text/Text';
import { useIntersectionObserver } from '@ulta/core/hooks/useIntersectionObserver/useIntersectionObserver';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { hasItems } from '@ulta/core/utils/array/array';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleIntersection } from '@ulta/core/utils/intersectionProcessor/intersectionProcessor';

import Tag from '@ulta/modules/Tag/Tag';

import PromotionalRailGrid from '../PromotionalRailGrid/PromotionalRailGrid';
import * as utils from './FeaturedRail';

/**
 * Represents a FeaturedRail component
 *
 * @method
 * @param {object} props - React properties passed from composition. See FeaturedRailProps.
 * @returns FeaturedRail
 */
export const FeaturedRail = function( props ){
  const {
    backgroundColor,
    modules,
    spotlight,
    textColor,
    smallBackgroundImage,
    mediumUpBackgroundImage,
    root,
    rootMargin,
    threshold
  } = props;
  const { breakpoint } = useDeviceInflection();
  const FeaturedRail = useRef( null );
  const isMobile = breakpoint?.isSmallDevice();

  useIntersectionObserver( FeaturedRail, {
    root,
    rootMargin,
    threshold
  }, handleIntersection( props ) );

  if( !hasItems( modules ) || !spotlight?.title ){
    return null;
  }

  const railContent = (
    <div className={ classNames( 'FeaturedRail__content', {
      [`FeaturedRail__backgroundColor--${ backgroundColor }`]: backgroundColor
    } ) }
    >
      <div className='FeaturedRail__spotlight'>
        { spotlight.urgencyTag && (
          <Tag
            { ...spreadProps( { ...spotlight.urgencyTag } ) }
            timerMeta={ spotlight.urgencyTag?.timerMeta ?? utils.DEFAULT_TIMER_META_CONFIG }
            size='default'
          />
        ) }
        <div className='FeaturedRail__spotlight-text'>
          { spotlight.eyebrow && (
            <div className='FeaturedRail__spotlight-eyebrow'>
              <Text
                textStyle='eyebrow'
                htmlTag='p'
                color={ textColor }
              >
                { spotlight.eyebrow }
              </Text>
            </div>
          ) }
          { spotlight.title && (
            <div className='FeaturedRail__spotlight-title'>
              <Text
                textStyle='display-sm'
                htmlTag='h2'
                color={ textColor }
              >
                { spotlight.title }
              </Text>
            </div>
          ) }
          { spotlight.subtitle && (
            <div className='FeaturedRail__spotlight-subtitle'>
              <Text
                htmlTag='p'
                color={ textColor }
                textStyle='bodyDefault'
              >
                { spotlight.subtitle }
              </Text>
            </div>
          ) }
        </div>
        { spotlight.linkAction && (
          <div className='FeaturedRail__spotlight-actions'>
            <ActionGroups
              variant='multi'
              types='button'
              actions={ spotlight.linkAction?.actions }
            />
          </div>
        ) }
      </div>
      <PromotionalRailGrid
        { ...spreadProps( { ...props } ) }
        cardType='Regular'
        items={ modules }
        isGrid={ false }
        title={ null }
        subtitle={ null }
        sliderConfig={ utils.SLIDER_CONFIG_SPOTLIGHT }
        spaceBetween={ utils.getSpaceBetween( breakpoint?.CURRENT_BREAKPOINT ) }
      />
    </div>
  );

  return (
    <div className='FeaturedRail' ref={ FeaturedRail }>
      <GridContainer fullBleedContent={
        isMobile && (
          <Image
            { ...spreadProps( { ...smallBackgroundImage } ) }
            src={ smallBackgroundImage?.imageUrl }
            isBackground
          >
            { railContent }
          </Image>
        ) }
      >
        { !isMobile && (
          <Image
            { ...spreadProps( { ...mediumUpBackgroundImage } ) }
            src={ mediumUpBackgroundImage?.imageUrl }
            isBackground
          >
            { railContent }
          </Image>
        ) }
      </GridContainer>
    </div>
  );
};

export const getSpaceBetween = ( aspectRatio ) => {
  switch ( aspectRatio ){
    case 'MD':
      return 8;
    case 'LG':
      return 12;
    case 'SM':
      // Fall through
    case 'XL':
      // Fall through
    default:
      return 16;
  }
};

export const SLIDER_CONFIG_SPOTLIGHT = {
  sliderPerViewXL: 4,
  slidesPerGroupXL: 4,
  sliderPerViewLG: 4,
  slidesPerGroupLG: 4,
  sliderPerViewMD: 3,
  slidesPerGroupMD: 3,
  sliderPerViewSM: 1.5,
  slidesPerGroupSM: 1
};

// TODO: This is to fix the issue in Palette Utility as null checks are missing for the timerMeta property.
export const DEFAULT_TIMER_META_CONFIG = {
  comingSoonLabel: '',
  extendedLabel: '',
  hoursLabel: '',
  todayOnlyLabel: '',
  todayAndTomorrowLabel: '',
  endsTomorrowLabel: '',
  endsTodayLabel: '',
  endsLabel: '',
  endsInLabel: '',
  offerEndedLabel: ''
};

/**
 * Property type definitions
 *
 * @typedef FeaturedRailProps
 * @type {object}
 * @property {string} title - Sets the headline title.
 * @property {string} subtitle - Sets the subtitle.
 * @property {array} modules - Set the module array.
 * @property {object} spotlight - The spotlight content for the rail.
 * @property {string} backgroundColor - Sets the backgroundColor
 * @property {string} alignment - sets the alignment for the text.
 * @property {boolean} isGrid - sets the value true/false based on value
 * @property {object} previousClickAction - The prop that holds the action for the previous button
 * @property {object} nextClickAction - The prop that holds the action for the next button.
 * @property {object} _railADAmeta - sets carouselAccessibility
 * @property {object} _railMeta - sets nextAccessibility, previousAccessibility
 */

export const propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  modules: PropTypes.array,
  spotlight: PropTypes.object,
  backgroundColor: PropTypes.string,
  isGrid: PropTypes.bool,
  _railADAmeta: PropTypes.object,
  _railMeta: PropTypes.object,
  previousClickAction: PropTypes.object,
  nextClickAction: PropTypes.object
};

FeaturedRail.propTypes = propTypes;

export default FeaturedRail;
